import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Download, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "../lib/i18n";

const ZOOMS = [7, 8, 9, 10, 11, 12];
const RADIUS_KM = 40;

function tileXY(lat, lng, z) {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const r = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n);
  return [x, y];
}

export function tileUrls(lat, lng) {
  const urls = [];
  const dLat = RADIUS_KM / 111;
  const dLng = RADIUS_KM / (111 * Math.cos((lat * Math.PI) / 180));
  for (const z of ZOOMS) {
    const [x0, y0] = tileXY(lat + dLat, lng - dLng, z);
    const [x1, y1] = tileXY(lat - dLat, lng + dLng, z);
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) urls.push(`https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`);
  }
  return urls;
}

/** Pre-downloads OSM tiles around the agent's zone so the map works without network (cached by the service worker). */
export function TilePrefetch({ zones, label }) {
  const { t } = useLang();
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(false);
  const targets = (zones || []).filter((z) => z.lat && z.lng);
  if (!targets.length || !("serviceWorker" in navigator)) return null;

  const run = async () => {
    const urls = [...new Set(targets.flatMap((z) => tileUrls(z.lat, z.lng)))];
    setProgress({ done: 0, total: urls.length }); setDone(false);
    let ok = 0;
    for (let i = 0; i < urls.length; i += 6) {
      await Promise.all(urls.slice(i, i + 6).map((u) => fetch(u, { mode: "no-cors" }).then(() => ok++).catch(() => null)));
      setProgress({ done: Math.min(i + 6, urls.length), total: urls.length });
    }
    setProgress(null); setDone(true);
    toast.success(`${ok} ${t("tuiles de carte conservées sur l'appareil.")}`);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="tile-prefetch">
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={!!progress} data-testid="tile-prefetch-btn">
        {progress ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : done ? <CheckCircle2 className="h-4 w-4 mr-1.5 text-emerald-600" /> : <Download className="h-4 w-4 mr-1.5" />}
        {progress ? `${progress.done}/${progress.total}` : t("Pré-charger la carte hors ligne")}
        {label && !progress ? ` · ${label}` : ""}
      </Button>
      <span className="text-xs text-slate-500">{t("Télécharge les fonds de carte de votre zone (~40 km) pour une consultation sans réseau.")}</span>
    </div>
  );
}

export default TilePrefetch;
